import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/lib/supabase";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isProcessingToken, setIsProcessingToken] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { updatePassword, user } = useAuth();

  const getParameterByName = (name: string, url = window.location.href) => {
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  };

  useEffect(() => {
    const handleHashChange = async () => {
      setIsProcessingToken(true);
      const accessToken = getParameterByName('access_token');
      const refreshToken = getParameterByName('refresh_token');
      const type = getParameterByName('type');
      
      console.log("URL params:", { accessToken, refreshToken, type });
      
      if (accessToken && type === 'recovery') {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });
          
          if (error) {
            console.error("Erreur lors de la définition de la session:", error);
            toast({
              title: "Erreur d'authentification",
              description: "Lien de réinitialisation invalide ou expiré. Veuillez demander un nouveau lien.",
              variant: "destructive",
            });
            navigate("/forgot-password");
            return;
          }
          
          console.log("Session établie avec succès:", data);
          setIsProcessingToken(false);
        } catch (error) {
          console.error("Exception lors de la définition de la session:", error);
          toast({
            title: "Erreur d'authentification",
            description: "Une erreur s'est produite lors de la tentative de réinitialisation. Veuillez réessayer.",
            variant: "destructive",
          });
          navigate("/forgot-password");
        }
      } else {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          console.log("Session existante détectée");
          setIsProcessingToken(false);
        } else {
          console.error("Aucun token de récupération ou session trouvé");
          toast({
            title: "Accès non autorisé",
            description: "Lien de réinitialisation invalide ou expiré. Veuillez demander un nouveau lien.",
            variant: "destructive",
          });
          navigate("/forgot-password");
        }
      }
    };

    handleHashChange();
  }, [location, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== passwordConfirm) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 8 caractères.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        throw error;
      }
      
      setIsSuccess(true);
      
      toast({
        title: "Mot de passe mis à jour",
        description: "Votre mot de passe a été réinitialisé avec succès.",
        variant: "default",
      });
      
      setTimeout(() => {
        supabase.auth.signOut();
        navigate("/login");
      }, 3000);
    } catch (error: any) {
      console.error("Erreur de réinitialisation du mot de passe:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de réinitialiser le mot de passe. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isProcessingToken) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-md text-center">
          <div className="animate-pulse">
            <div className="h-8 w-1/2 mx-auto bg-gray-200 rounded mb-4"></div>
            <div className="h-4 w-3/4 mx-auto bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-2/3 mx-auto bg-gray-200 rounded"></div>
          </div>
          <p className="text-sm text-gray-500 mt-4">Vérification de votre lien de réinitialisation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-md">
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src="/lovable-uploads/3f5ed62f-2b52-4da4-bd9c-ec9e16d3d796.png" alt="Scaloo" className="h-10 mx-auto" />
          </Link>
          
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Réinitialiser votre mot de passe
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Choisissez un nouveau mot de passe sécurisé pour votre compte.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {!isSuccess ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <Label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Nouveau mot de passe
                </Label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
                    <Lock className="h-4 w-4" />
                  </span>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="block w-full flex-1 rounded-none rounded-r-md border-gray-300 focus:border-socialsync-500 focus:ring-socialsync-500 sm:text-sm"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700">
                  Confirmer le mot de passe
                </Label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
                    <Lock className="h-4 w-4" />
                  </span>
                  <Input
                    id="passwordConfirm"
                    name="passwordConfirm"
                    type="password"
                    required
                    className="block w-full flex-1 rounded-none rounded-r-md border-gray-300 focus:border-socialsync-500 focus:ring-socialsync-500 sm:text-sm"
                    placeholder="••••••••"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    minLength={8}
                  />
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  className="flex w-full justify-center rounded-md border border-transparent bg-socialsync-600 px-4 py-2 text-sm font-medium text-white hover:bg-socialsync-700 focus:outline-none focus:ring-2 focus:ring-socialsync-500 focus:ring-offset-2"
                  disabled={isLoading}
                >
                  {isLoading ? "Mise à jour..." : "Réinitialiser le mot de passe"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-6 text-center">
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      Mot de passe mis à jour avec succès
                    </p>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-500">
                Vous serez redirigé vers la page de connexion dans quelques secondes...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
